import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { UserAccessModel } from '../models/user-access.model';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { RoleModel } from '../models/role.model';
import { GroupModel } from '../models/group.model';
import { PermissionModel } from '../models/permission.model';
import { NotificationModel } from '../models/notification.model';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private apiService: ApiService,
    private authService: AuthService,
  ) {}

  listAllUsers(groupId?: any): Observable<UserAccessModel[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        const params = groupId ? { groupId } : {};
        return this.apiService.get<UserAccessModel[]>(
          environment.userAccess,
          params,
          {
            Authorization: `Bearer ${token}`,
          },
        );
      }),
    );
  }

  getUserById(id: string): Observable<UserAccessModel> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        const url = `${environment.userAccess}/${id}`;
        return this.apiService.get<UserAccessModel>(
          url,
          {},
          {
            Authorization: `Bearer ${token}`,
          },
        );
      }),
    );
  }

  getGroups(): Observable<GroupModel[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) =>
        this.apiService.get<GroupModel[]>(
          environment.groups,
          {},
          {
            Authorization: `Bearer ${token}`,
          },
        ),
      ),
    );
  }

  updateUser(user: UserAccessModel): Observable<UserAccessModel> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) =>
        this.apiService.put<UserAccessModel>(environment.userAccess, user, {
          Authorization: `Bearer ${token}`,
        }),
      ),
    );
  }

  createUser(
    user: UserAccessModel,
  ): Observable<{ id: string; password: string }> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) =>
        this.apiService.post<{ id: string; password: string }>(
          environment.userAccess,
          user,
          {
            Authorization: `Bearer ${token}`,
          },
        ),
      ),
    );
  }

  getRolesByGroupId(customerId: any): Observable<RoleModel[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) =>
        this.apiService.get<RoleModel[]>(
          environment.roles,
          {
            GroupId: customerId,
          },
          {
            Authorization: `Bearer ${token}`,
          },
        ),
      ),
    );
  }

  createRole(data: {
    name: string;
    description: string;
    azureGroupId: string;
    user: string;
  }): Observable<any> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) =>
        this.apiService.post<any>(environment.roles, data, {
          Authorization: `Bearer ${token}`,
        }),
      ),
    );
  }

  updateRole(data: {
    name: string;
    description: string;
    azureGroupId: string;
    user: string;
    id: string;
    active: number;
  }): Observable<any> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) =>
        this.apiService.put<any>(environment.roles, data, {
          Authorization: `Bearer ${token}`,
        }),
      ),
    );
  }

  getPermissions(CustomerId: any): Observable<PermissionModel[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) =>
        this.apiService.get<PermissionModel[]>(
          environment.permissions,
          {
            CustomerId,
          },
          {
            Authorization: `Bearer ${token}`,
          },
        ),
      ),
    );
  }

  getPermsByRole(RoleId: string): Observable<PermissionModel[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) =>
        this.apiService.get<PermissionModel[]>(
          environment.permRole,
          {
            RoleId,
          },
          {
            Authorization: `Bearer ${token}`,
          },
        ),
      ),
    );
  }

  assignPermsToRole(data: {
    RoleId: string;
    PermissionIds: string[];
    CreatedBy: string;
  }): Observable<any> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) =>
        this.apiService.post<any>(environment.permRole, data, {
          Authorization: `Bearer ${token}`,
        }),
      ),
    );
  }

  getNotifications(userId: string): Observable<NotificationModel[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) =>
        this.apiService.get<NotificationModel[]>(
          environment.notification,
          {
            userid: userId,
          },
          {
            Authorization: `Bearer ${token}`,
          },
        ),
      ),
    );
  }

  dismissNotification(notification: NotificationModel): Observable<any> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) =>
        this.apiService.put<any>(environment.notification, notification, {
          Authorization: `Bearer ${token}`,
        }),
      ),
    );
  }
}
